import useUiTranslation from "../utils/useUiTranslation";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Search,
  Plus,
  ZoomIn,
  ZoomOut,
  Maximize2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import * as org from "../api/organization";
import { useAuth } from "../hooks/useAuth";
import { hasPermission } from "../utils/permissions";

const message = (e) =>
  e.response?.data?.message ||
  e.message ||
  "The request could not be completed";
const subtreeMatches = (node, q) =>
  !q ||
  `${node.name} ${node.code || ""}`
    .toLowerCase()
    .includes(String(q).toLowerCase()) ||
  (node.children || []).some((c) => subtreeMatches(c, q));
const flatten = (tree) => {
  const out = [];
  const walk = (xs, parent, path = []) =>
    xs.forEach((x) => {
      const item = {
        ...x,
        parentName: parent?.name,
        displayPath: [...path, x.name],
      };
      out.push(item);
      walk(x.children || [], item, item.displayPath);
    });
  walk(tree);
  return out;
};
const validParentTypes = {
  INSTITUTE: [],
  EXECUTIVE_OFFICE: ["INSTITUTE", "EXECUTIVE_OFFICE"],
  DIRECTORATE: ["INSTITUTE", "EXECUTIVE_OFFICE", "DIRECTORATE"],
  DEPARTMENT: ["INSTITUTE", "EXECUTIVE_OFFICE", "DIRECTORATE"],
  SECTION: ["DEPARTMENT"],
  UNIT: [
    "INSTITUTE",
    "EXECUTIVE_OFFICE",
    "DIRECTORATE",
    "DEPARTMENT",
    "SECTION",
    "UNIT",
  ],
};
const withDisplayPaths = (units) => {
  const byId = new Map(units.map((unit) => [String(unit.id), unit]));
  const pathFor = (unit, visited = new Set()) => {
    if (!unit || visited.has(String(unit.id))) return [];
    const next = new Set(visited).add(String(unit.id));
    return [...pathFor(byId.get(String(unit.parent_unit_id)), next), unit.name];
  };
  return units.map((unit) => ({ ...unit, displayPath: pathFor(unit) }));
};
const canParent = (unitType, parent) =>
  !parent || validParentTypes[unitType]?.includes(parent.unit_type);
const descendantIds = (unitId, units) => {
  const childrenByParent = new Map();
  units.forEach((unit) => {
    const key = String(unit.parent_unit_id ?? "");
    childrenByParent.set(key, [...(childrenByParent.get(key) || []), unit]);
  });
  const ids = new Set();
  const visit = (id) =>
    (childrenByParent.get(String(id)) || []).forEach((child) => {
      ids.add(String(child.id));
      visit(child.id);
    });
  visit(unitId);
  return ids;
};
const dateOnly = (value) => (value ? String(value).slice(0, 10) : "");
const positionStatus = (p, today = new Date().toISOString().slice(0, 10)) =>
  !p.is_active
    ? "ARCHIVED"
    : !p.user_id
      ? "VACANT"
      : p.effective_from && dateOnly(p.effective_from) > dateOnly(today)
        ? "FUTURE"
        : p.effective_to && dateOnly(p.effective_to) < dateOnly(today)
          ? "EXPIRED"
          : "ACTIVE";

function Dialog({ title, children, onClose }) {
  return (
    <div className="org-modal-backdrop" role="presentation">
      <section
        className="org-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <button aria-label="Close" className="close" onClick={onClose}>
          ×
        </button>
        <h2>{title}</h2>
        {children}
      </section>
    </div>
  );
}
function ErrorText({ error }) {
  return error ? (
    <p className="form-error" role="alert">
      {error}
    </p>
  ) : null;
}
function Field({ label, children }) {
  return (
    <label className="org-field">
      <span>{label}</span>
      {children}
    </label>
  );
}
function UnitForm({ mode, unit, units, options, onClose, onSaved }) {
  const uiText = useUiTranslation();
  const [form, setForm] = useState({
    name: unit?.name || "",
    code: unit?.code || "",
    unitType: unit?.unit_type || "UNIT",
    parentUnitId: unit?.parent_unit_id || "",
    classification: unit?.classification || "",
    sortOrder: unit?.sort_order ?? 0,
    departmentId: "",
    sectionId: "",
  });
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setType = (unitType) =>
    setForm((f) => ({
      ...f,
      unitType,
      parentUnitId: canParent(
        unitType,
        units.find((x) => String(x.id) === String(f.parentUnitId)),
      )
        ? f.parentUnitId
        : "",
    }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError("Name is required");
    setBusy(true);
    setError("");
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code || null,
        classification: form.classification || null,
        sortOrder: Number(form.sortOrder) || 0,
      };
      let saved;
      if (mode === "add")
        saved = await org.createOrganizationUnit({
          ...payload,
          unitType: form.unitType,
          parentUnitId: form.parentUnitId || null,
          departmentId:
            form.unitType === "DEPARTMENT" ? form.departmentId || null : null,
          sectionId:
            form.unitType === "SECTION" ? form.sectionId || null : null,
        });
      else saved = await org.updateOrganizationUnit(unit.id, payload);
      await onSaved(saved);
    } catch (err) {
      setError(message(err));
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={
        mode === "add" ? "Add organization unit" : "Edit organization unit"
      }
      onClose={onClose}
    >
      <form onSubmit={submit}>
        <Field label={uiText("Name")}>
          <input
            aria-label={uiText("Name")}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            required
          />
        </Field>
        <Field label="Code">
          <input
            aria-label="Code"
            value={form.code}
            onChange={(e) => set("code", e.target.value)}
          />
        </Field>
        {mode === "add" && (
          <>
            <Field label="Unit type">
              <select
                aria-label="Unit type"
                value={form.unitType}
                onChange={(e) => setType(e.target.value)}
              >
                {[
                  "INSTITUTE",
                  "EXECUTIVE_OFFICE",
                  "DIRECTORATE",
                  "DEPARTMENT",
                  "SECTION",
                  "UNIT",
                ].map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </Field>
            <Field label="Parent">
              <select
                aria-label="Parent"
                value={form.parentUnitId}
                onChange={(e) => set("parentUnitId", e.target.value)}
              >
                <option value="">Root</option>
                {units
                  .filter((x) => x.is_active && canParent(form.unitType, x))
                  .map((x) => (
                    <option value={x.id} key={x.id}>
                      {x.displayPath.join(" › ")}
                    </option>
                  ))}
              </select>
            </Field>
            {form.unitType === "DEPARTMENT" && (
              <Field label={uiText("Department")}>
                <select
                  aria-label={uiText("Department")}
                  value={form.departmentId}
                  onChange={(e) => set("departmentId", e.target.value)}
                  required
                >
                  <option value="">Search or select a department</option>
                  {options.departments.map((x) => (
                    <option value={x.id} key={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
            {form.unitType === "SECTION" && (
              <Field label={uiText("Section")}>
                <select
                  aria-label={uiText("Section")}
                  value={form.sectionId}
                  onChange={(e) => set("sectionId", e.target.value)}
                  required
                >
                  <option value="">Search or select a section</option>
                  {options.sections.map((x) => (
                    <option value={x.id} key={x.id}>
                      {x.department_name} — {x.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </>
        )}
        <Field label="Classification">
          <input
            aria-label="Classification"
            value={form.classification}
            onChange={(e) => set("classification", e.target.value)}
          />
        </Field>
        <Field label={uiText("Sort order")}>
          <input
            aria-label={uiText("Sort order")}
            type="number"
            value={form.sortOrder}
            onChange={(e) => set("sortOrder", e.target.value)}
          />
        </Field>
        <ErrorText error={error} />
        <div className="modal-actions">
          <button type="button" onClick={onClose}>{uiText("Cancel")}</button>
          <button className="primary" disabled={busy}>
            {busy ? uiText("Saving…") : uiText("Save")}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
function MoveDialog({ unit, units, onClose, onSaved }) {
  const uiText = useUiTranslation();
  const [target, setTarget] = useState(unit.parent_unit_id || ""),
    [confirm, setConfirm] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const parent = units.find(
    (x) => String(x.id) === String(unit.parent_unit_id),
  );
  const next = units.find((x) => String(x.id) === String(target));
  const descendants = descendantIds(unit.id, units);
  const availableParents = units.filter(
    (candidate) =>
      candidate.is_active &&
      String(candidate.id) !== String(unit.id) &&
      !descendants.has(String(candidate.id)) &&
      canParent(unit.unit_type, candidate),
  );
  const unchanged = String(target || "") === String(unit.parent_unit_id || "");
  const submit = async (e) => {
    e.preventDefault();
    if (!confirm) return setError("Confirm the hierarchy change to continue");
    setBusy(true);
    try {
      await org.moveOrganizationUnit(unit.id, target || null);
      await onSaved(unit);
    } catch (err) {
      setError(message(err));
      setBusy(false);
    }
  };
  return (
    <Dialog title="Move organization unit" onClose={onClose}>
      <form onSubmit={submit}>
        <dl>
          <dt>Current parent</dt>
          <dd>{parent?.name || unit.parent_name || "Root"}</dd>
          <dt>Current organizational path</dt>
          <dd>{unit.path?.join(" › ") || unit.name}</dd>
        </dl>
        <Field label="Target parent">
          <select
            aria-label="Target parent"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          >
            <option value="">Root</option>
            {availableParents.map((x) => (
              <option key={x.id} value={x.id}>
                {x.displayPath.join(" › ")}
              </option>
            ))}
          </select>
        </Field>
        <p>
          <strong>New path preview:</strong>{" "}
          {[...(next?.displayPath || []), unit.name].join(" › ")}
        </p>
        <p className="org-warning">
          Moving this unit changes its reporting hierarchy for future resolver
          use. Historical approvals are not changed.
        </p>
        <label>
          <input
            type="checkbox"
            checked={confirm}
            onChange={(e) => setConfirm(e.target.checked)}
          />{" "}
          I confirm this move
        </label>
        <ErrorText error={error} />
        <div className="modal-actions">
          <button type="button" onClick={onClose}>{uiText("Cancel")}</button>
          <button className="primary" disabled={busy || unchanged}>
            {busy ? "Moving…" : "Move unit"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
function HeadDialog({ unit, options, onClose, onSaved }) {
  const uiText = useUiTranslation();
  const [form, setForm] = useState({
      userId: "",
      effectiveFrom: new Date().toISOString().slice(0, 10),
      effectiveTo: "",
    }),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await org.assignOrganizationHead(unit.id, {
        ...form,
        effectiveTo: form.effectiveTo || null,
      });
      await onSaved(unit);
    } catch (err) {
      setError(message(err));
      setBusy(false);
    }
  };
  return (
    <Dialog title={`Assign / Change Head — ${unit.name}`} onClose={onClose}>
      <form onSubmit={submit}>
        <dl>
          <dt>Current Head</dt>
          <dd>
            {unit.unitHead?.status === "RESOLVED"
              ? unit.unitHead.userName
              : unit.unitHead?.status === "AMBIGUOUS"
                ? "Ambiguous"
                : uiText("Unassigned")}
          </dd>
          <dt>Current effective period</dt>
          <dd>
            {unit.unitHead?.status === "RESOLVED"
              ? `${unit.unitHead.effectiveFrom || uiText("Open")} — ${unit.unitHead.effectiveTo || uiText("Open")}`
              : "None"}
          </dd>
          <dt>Incoming Head</dt>
          <dd>
            {options.users.find((x) => String(x.id) === String(form.userId))
              ?.name || "Choose a user"}
          </dd>
          <dt>Effective From</dt>
          <dd>{form.effectiveFrom}</dd>
        </dl>
        <p>
          {form.effectiveFrom > new Date().toISOString().slice(0, 10)
            ? "The current head remains effective until the incoming head starts."
            : "The current authority ends when this replacement takes effect."}{" "}
          Roles and permissions are unchanged.
        </p>
        <Field label="Active institute user">
          <select
            aria-label="Active institute user"
            required
            value={form.userId}
            onChange={(e) => setForm({ ...form, userId: e.target.value })}
          >
            <option value="">Choose a user</option>
            {options.users.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} ({x.email})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Effective from">
          <input
            aria-label="Head effective from"
            type="date"
            value={form.effectiveFrom}
            onChange={(e) =>
              setForm({ ...form, effectiveFrom: e.target.value })
            }
          />
        </Field>
        <Field label="Effective to (optional)">
          <input
            aria-label="Head effective to"
            type="date"
            value={form.effectiveTo}
            onChange={(e) => setForm({ ...form, effectiveTo: e.target.value })}
          />
        </Field>
        <ErrorText error={error} />
        <div className="modal-actions">
          <button type="button" onClick={onClose}>{uiText("Cancel")}</button>
          <button className="primary" disabled={busy}>
            {busy ? "Assigning…" : "Assign head"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
function ReconciliationDialog({ onClose, onChanged }) {
  const uiText = useUiTranslation();
  const [rows, setRows] = useState([]),
    [busy, setBusy] = useState(true),
    [error, setError] = useState(""),
    [reason, setReason] = useState("");
  const load = useCallback(() => {
    setBusy(true);
    org
      .getHeadReconciliation()
      .then(setRows)
      .catch((e) => setError(message(e)))
      .finally(() => setBusy(false));
  }, []);
  useEffect(load, [load]);
  const assign = async (row) => {
    try {
      await org.reconcileOrganizationHead(row.unitId, {
        userId: row.legacyCandidates[0].id,
        fromLegacy: true,
      });
      load();
      onChanged();
    } catch (e) {
      setError(message(e));
    }
  };
  const decide = async (row, decision) => {
    if (!reason.trim()) return setError("A reason is required");
    try {
      await org.resolveHeadReconciliation(row.unitId, {
        decision,
        reason: reason.trim(),
      });
      setReason("");
      load();
      onChanged();
    } catch (e) {
      setError(message(e));
    }
  };
  const bulk = async () => {
    const safe = rows.filter((x) => x.safeToAssign);
    if (!safe.length) return;
    try {
      await org.bulkReconcileHeads(safe.map((x) => x.unitId));
      load();
      onChanged();
    } catch (e) {
      setError(message(e));
    }
  };
  return (
    <Dialog title="Head Reconciliation" onClose={onClose}>
      <p>
        Legacy HOD evidence is discovery-only until an administrator confirms an
        assignment. Existing organization heads are never overwritten.
      </p>
      {busy ? (
        <p>Loading reconciliation…</p>
      ) : (
        <>
          <button
            className="primary"
            disabled={!rows.some((x) => x.safeToAssign)}
            onClick={bulk}
          >
            Bulk Assign Safe Matches
          </button>
          <div className="org-table">
            <table>
              <thead>
                <tr>
                  <th>{uiText("Department")}</th>
                  <th>Legacy HOD user(s)</th>
                  <th>Organization head</th>
                  <th>{uiText("Status")}</th>
                  <th>Suggested action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.unitId}>
                    <td>{row.department.name}</td>
                    <td>
                      {row.legacyCandidates.length
                        ? row.legacyCandidates.map((x) => (
                            <span key={x.id}>
                              {x.name}
                              <small>{x.evidence.join(", ")}</small>
                            </span>
                          ))
                        : "None"}
                    </td>
                    <td>{row.currentOrganizationHead?.user_name || "None"}</td>
                    <td>
                      <span className="status">{row.status}</span>
                    </td>
                    <td>
                      {row.safeToAssign ? (
                        <button onClick={() => assign(row)}>
                          Assign Suggested Head
                        </button>
                      ) : row.status === "CONFLICT" ? (
                        <div>
                          <Field label={uiText("Reason")}>
                            <input
                              aria-label={`Decision reason ${row.department.name}`}
                              value={reason}
                              onChange={(e) => setReason(e.target.value)}
                            />
                          </Field>
                          <button onClick={() => decide(row, "KEEP_EXISTING")}>
                            Keep Existing Organization Head
                          </button>
                          <button
                            onClick={() => decide(row, "MARK_LEGACY_OBSOLETE")}
                          >
                            Mark Legacy Data Obsolete
                          </button>
                        </div>
                      ) : (
                        row.suggestedAction
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <ErrorText error={error} />
    </Dialog>
  );
}
function HealthPanel({
  units,
  conflicts = [],
  onSelect,
  onReconcile,
  canManage,
}) {
  const missing = (type) =>
    units.filter(
      (x) =>
        x.is_active &&
        x.unit_type === type &&
        x.unitHead?.status !== "RESOLVED",
    );
  const metrics = [
    ["Active units", units.filter((x) => x.is_active)],
    [
      "Units without parent",
      units.filter(
        (x) => x.is_active && !x.parent_unit_id && x.unit_type !== "INSTITUTE",
      ),
    ],
    ["Departments without head", missing("DEPARTMENT")],
    ["Sections without head", missing("SECTION")],
    ["Executive offices without head", missing("EXECUTIVE_OFFICE")],
    ["Ambiguous head assignments", units.filter((x) => x.authorityError)],
    [
      "Inactive holder assignments",
      units.filter((x) =>
        (x.positions || []).some((p) => !p.is_active && p.user_id),
      ),
    ],
    [
      "Future-only head assignments",
      units.filter((x) =>
        (x.positions || []).some((p) => positionStatus(p) === "FUTURE"),
      ),
    ],
    [
      "Legacy HOD conflicts",
      units.filter((x) => conflicts.map(String).includes(String(x.id))),
    ],
  ];
  return (
    <section className="org-health">
      <header>
        <div>
          <h2>Configuration health</h2>
          <small>Hierarchy readiness, not a procurement KPI</small>
        </div>
        {canManage && (
          <button onClick={onReconcile}>Head Reconciliation →</button>
        )}
      </header>
      <div>
        {metrics.map(([label, affected], index) => (
          <button
            key={label}
            type="button"
            disabled={!affected.length || index === 0}
            aria-label={
              index === 0
                ? `${affected.length} ${label}`
                : `${label}: ${affected.length}${affected.length ? "; show first affected unit" : ""}`
            }
            onClick={() => affected[0] && onSelect(affected[0])}
          >
            <strong>{affected.length}</strong>
            <span>{label}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
function PositionsDialog({ unit, options, onClose, onChanged }) {
  const uiText = useUiTranslation();
  const empty = {
    positionType: "CUSTOM",
    positionName: "",
    userId: "",
    effectiveFrom: "",
    effectiveTo: "",
    isActive: true,
  };
  const [positions, setPositions] = useState(unit.positions || []),
    [form, setForm] = useState(empty),
    [editing, setEditing] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  useEffect(() => {
    org
      .getOrganizationPositions(unit.id)
      .then(setPositions)
      .catch((e) => setError(message(e)));
  }, [unit.id]);
  const edit = (p) => {
    setEditing(p.id);
    setForm({
      positionType: p.position_type,
      positionName: p.position_name,
      userId: p.user_id || "",
      effectiveFrom: p.effective_from?.slice(0, 10) || "",
      effectiveTo: p.effective_to?.slice(0, 10) || "",
      isActive: p.is_active,
    });
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = {
        ...form,
        userId: form.userId || null,
        effectiveFrom: form.effectiveFrom || null,
        effectiveTo: form.effectiveTo || null,
      };
      editing
        ? await org.updateOrganizationPosition(editing, payload)
        : await org.createOrganizationPosition(unit.id, payload);
      setPositions(await org.getOrganizationPositions(unit.id));
      setForm(empty);
      setEditing(null);
      setBusy(false);
      onChanged();
    } catch (err) {
      setError(
        err.response?.status === 409
          ? `Configuration error: ${message(err)}`
          : message(err),
      );
      setBusy(false);
    }
  };
  const archive = async (p) => {
    if (!window.confirm(`Archive ${p.position_name}?`)) return;
    try {
      await org.archiveOrganizationPosition(p.id);
      setPositions(await org.getOrganizationPositions(unit.id));
      onChanged();
    } catch (e) {
      setError(message(e));
    }
  };
  return (
    <Dialog title={`Manage positions — ${unit.name}`} onClose={onClose}>
      <div className="position-list">
        {positions.length === 0 ? (
          <p>No positions configured.</p>
        ) : (
          positions.map((p) => (
            <div key={p.id}>
              <div>
                <strong>{p.position_name}</strong>
                <span className={`status ${positionStatus(p).toLowerCase()}`}>
                  {positionStatus(p)}
                </span>
                <small>
                  {p.user_name || uiText("Unassigned")} · {p.position_type}
                </small>
              </div>
              <button onClick={() => edit(p)}>{uiText("Edit")}</button>
              <button onClick={() => archive(p)}>Archive</button>
            </div>
          ))
        )}
      </div>
      <form onSubmit={save}>
        <h3>{editing ? "Edit position" : "Add position"}</h3>
        <Field label="Position type">
          <select
            aria-label="Position type"
            value={form.positionType}
            onChange={(e) => setForm({ ...form, positionType: e.target.value })}
          >
            {[
              "UNIT_HEAD",
              "EXECUTIVE_HEAD",
              "DEPARTMENT_HEAD",
              "SECTION_HEAD",
              "CUSTOM",
            ].map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </Field>
        <Field label="Position name">
          <input
            aria-label="Position name"
            required
            value={form.positionName}
            onChange={(e) => setForm({ ...form, positionName: e.target.value })}
          />
        </Field>
        <Field label="User / holder">
          <select
            aria-label="User / holder"
            value={form.userId}
            onChange={(e) => setForm({ ...form, userId: e.target.value })}
          >
            <option value="">Vacant</option>
            {options.users.map((x) => (
              <option value={x.id} key={x.id}>
                {x.name} ({x.email})
              </option>
            ))}
          </select>
        </Field>
        <p className="org-field-help">
          Head authority is assigned automatically when the position type
          matches this unit's canonical head type.
        </p>
        <Field label="Effective from">
          <input
            aria-label="Effective from"
            type="date"
            value={form.effectiveFrom}
            onChange={(e) =>
              setForm({ ...form, effectiveFrom: e.target.value })
            }
          />
        </Field>
        <Field label="Effective to">
          <input
            aria-label="Effective to"
            type="date"
            value={form.effectiveTo}
            onChange={(e) => setForm({ ...form, effectiveTo: e.target.value })}
          />
        </Field>
        <label>
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          />{" "}{uiText("Active")}</label>
        <ErrorText error={error} />
        <button className="primary" disabled={busy}>
          {busy ? uiText("Saving…") : editing ? "Save position" : "Add position"}
        </button>
      </form>
    </Dialog>
  );
}
function Node({
  node,
  selected,
  setSelected,
  collapsed,
  setCollapsed,
  query,
  canManage,
  draggedUnit,
  dropTarget,
  setDraggedUnit,
  setDropTarget,
  onDropUnit,
}) {
  const uiText = useUiTranslation();
  if (!subtreeMatches(node, query)) return null;
  const closed = collapsed.has(node.id);
  const isDragged = String(draggedUnit?.id) === String(node.id);
  const isDropTarget = String(dropTarget?.id) === String(node.id);
  const validDrop = draggedUnit && canParent(draggedUnit.unit_type, node);
  return (
    <li>
      <button
        className={`org-node ${selected?.id === node.id ? "selected" : ""} ${!node.is_active ? "archived" : ""} ${isDragged ? "dragging" : ""} ${isDropTarget ? (validDrop ? "drop-target" : "drop-invalid") : ""}`}
        onClick={() => setSelected(node)}
        draggable={canManage && node.is_active}
        aria-label={`${node.name}${canManage && node.is_active ? "; drag to move" : ""}`}
        onDragStart={(event) => {
          event.stopPropagation();
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData("text/plain", String(node.id));
          setDraggedUnit(node);
        }}
        onDragEnd={() => {
          setDraggedUnit(null);
          setDropTarget(null);
        }}
        onDragEnter={(event) => {
          if (!draggedUnit || isDragged) return;
          event.preventDefault();
          setDropTarget(node);
        }}
        onDragOver={(event) => {
          if (!draggedUnit || isDragged) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = validDrop ? "move" : "none";
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget))
            setDropTarget(null);
        }}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onDropUnit(node);
        }}
      >
        <span
          onClick={(e) => {
            e.stopPropagation();
            setCollapsed((s) => {
              const n = new Set(s);
              n.has(node.id) ? n.delete(node.id) : n.add(node.id);
              return n;
            });
          }}
        >
          {node.children?.length ? (
            closed ? (
              <ChevronRight />
            ) : (
              <ChevronDown />
            )
          ) : null}
        </span>
        <strong>{node.name}</strong>
        <small>{node.unit_type.replaceAll("_", " ")}</small>
        {node.classification && <em>{node.classification}</em>}
        <small>
          Head:{" "}
          {node.unitHead?.status === "RESOLVED"
            ? node.unitHead.userName
            : node.unitHead?.status === "AMBIGUOUS"
              ? "Ambiguous"
              : uiText("Unassigned")}
        </small>
        {!node.is_active && <small>ARCHIVED</small>}
      </button>
      {!closed && node.children?.length > 0 && (
        <ul>
          {node.children.map((c) => (
            <Node
              key={c.id}
              {...{
                node: c,
                selected,
                setSelected,
                collapsed,
                setCollapsed,
                query,
                canManage,
                draggedUnit,
                dropTarget,
                setDraggedUnit,
                setDropTarget,
                onDropUnit,
              }}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
export default function OrganizationHierarchy() {
  const uiText = useUiTranslation();
  const { user } = useAuth();
  const canManage = hasPermission(user, "organization.manage");
  const [state, setState] = useState("loading"),
    [error, setError] = useState(""),
    [tree, setTree] = useState([]),
    [parentUnits, setParentUnits] = useState([]),
    [selected, setSelected] = useState(),
    [collapsed, setCollapsed] = useState(new Set()),
    [query, setQuery] = useState(""),
    [zoom, setZoom] = useState(1),
    [draggedUnit, setDraggedUnit] = useState(null),
    [dropTarget, setDropTarget] = useState(null),
    [movingUnitId, setMovingUnitId] = useState(null),
    [view, setView] = useState("tree"),
    [dialog, setDialog] = useState(),
    [options, setOptions] = useState({
      departments: [],
      sections: [],
      users: [],
    }),
    [health, setHealth] = useState({ legacyHodConflicts: [] });
  const canvasRef = useRef(null);
  const panRef = useRef(null);
  const flat = useMemo(() => flatten(tree), [tree]);
  const visibleCount = useMemo(
    () => flat.filter((unit) => subtreeMatches(unit, query)).length,
    [flat, query],
  );
  const refreshHealth = useCallback(() => {
    org
      .getOrganizationHealth()
      .then(setHealth)
      .catch(() => {});
  }, []);
  const load = useCallback(async (selectId) => {
    setState("loading");
    setError("");
    try {
      const [data, units] = await Promise.all([
        org.getOrganizationTree(),
        org.getOrganizationUnits(),
      ]);
      setTree(data);
      setParentUnits(withDisplayPaths(units));
      setState("ready");
      if (selectId) setSelected(await org.getOrganizationUnit(selectId));
    } catch (e) {
      setError(message(e));
      setState("error");
    }
  }, []);
  useEffect(() => {
    load();
    org
      .getOrganizationOptions()
      .then(setOptions)
      .catch(() => {});
    refreshHealth();
  }, [load, refreshHealth]);
  useEffect(() => {
    if (selected?.id)
      org
        .getOrganizationUnit(selected.id)
        .then(setSelected)
        .catch((e) => setError(message(e)));
  }, [selected?.id]);
  const saved = async (unit) => {
    setDialog(null);
    await load(unit.id);
    refreshHealth();
  };
  const moveByDrop = async (parent) => {
    if (!draggedUnit || movingUnitId) return;
    const descendants = descendantIds(draggedUnit.id, parentUnits);
    const invalid =
      String(draggedUnit.id) === String(parent.id) ||
      descendants.has(String(parent.id)) ||
      !parent.is_active ||
      !canParent(draggedUnit.unit_type, parent);
    setDropTarget(null);
    if (invalid) {
      setError(`${draggedUnit.name} cannot report to ${parent.name}`);
      return;
    }
    if (String(draggedUnit.parent_unit_id) === String(parent.id)) return;
    setMovingUnitId(draggedUnit.id);
    setError("");
    try {
      await org.moveOrganizationUnit(draggedUnit.id, parent.id);
      await load(draggedUnit.id);
      refreshHealth();
    } catch (e) {
      setError(message(e));
    } finally {
      setMovingUnitId(null);
      setDraggedUnit(null);
    }
  };
  const startPan = (event) => {
    if ((event.button ?? 0) !== 0 || event.target.closest("button")) return;
    const canvas = canvasRef.current;
    panRef.current = {
      x: event.clientX,
      y: event.clientY,
      left: canvas.scrollLeft,
      top: canvas.scrollTop,
    };
    canvas.classList.add("panning");
    canvas.setPointerCapture?.(event.pointerId);
  };
  const pan = (event) => {
    if (!panRef.current) return;
    const canvas = canvasRef.current;
    canvas.scrollLeft =
      panRef.current.left - (event.clientX - panRef.current.x);
    canvas.scrollTop = panRef.current.top - (event.clientY - panRef.current.y);
  };
  const stopPan = (event) => {
    panRef.current = null;
    canvasRef.current?.classList.remove("panning");
    if (canvasRef.current?.hasPointerCapture?.(event.pointerId))
      canvasRef.current.releasePointerCapture(event.pointerId);
  };
  const archive = async () => {
    if (!window.confirm(`Archive ${selected.name}? This does not delete it.`))
      return;
    try {
      await org.archiveOrganizationUnit(selected.id);
      await load(selected.id);
      refreshHealth();
    } catch (e) {
      setError(message(e));
    }
  };
  return (
    <main className="org-page">
      <header>
        <div>
          <h1>Organization Hierarchy</h1>
          <p>
            Reporting structure is maintained independently from procurement
            approval routes.
          </p>
        </div>
        {canManage && (
          <button className="primary" onClick={() => setDialog("add")}>
            <Plus /> Add Unit
          </button>
        )}
      </header>
      <HealthPanel
        units={flat}
        conflicts={health.legacyHodConflicts}
        canManage={canManage}
        onSelect={(x) => setSelected(x)}
        onReconcile={() => setDialog("reconciliation")}
      />
      <div className="org-toolbar">
        <label>
          <Search />
          <input
            aria-label="Search units"
            placeholder="Search units"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              type="button"
              className="org-search-clear"
              aria-label="Clear search input"
              onClick={() => setQuery("")}
            >
              ×
            </button>
          )}
        </label>
        <button onClick={() => setCollapsed(new Set())}>Expand All</button>
        <button onClick={() => setCollapsed(new Set(flat.map((x) => x.id)))}>
          Collapse All
        </button>
        <button onClick={() => setView(view === "tree" ? "table" : "tree")}>
          {view === "tree" ? uiText("Table view") : "Tree view"}
        </button>
        <span className="org-result-count" aria-live="polite">
          {visibleCount} {visibleCount === 1 ? "unit" : "units"}
        </span>
        {view === "tree" && (
          <>
            <button
              aria-label="Zoom in"
              onClick={() => setZoom((z) => Math.min(1.5, z + 0.1))}
            >
              <ZoomIn />
            </button>
            <button
              aria-label="Zoom out"
              onClick={() => setZoom((z) => Math.max(0.6, z - 0.1))}
            >
              <ZoomOut />
            </button>
            <button aria-label="Reset zoom" onClick={() => setZoom(1)}>
              <Maximize2 />
            </button>
          </>
        )}
      </div>
      {movingUnitId && (
        <p className="org-move-status" role="status">
          Moving unit…
        </p>
      )}
      {error && state !== "error" && <p role="alert">{error}</p>}
      {state === "loading" && <p>Loading organization hierarchy…</p>}
      {state === "error" && <p role="alert">{error}</p>}
      {state === "error" && <button onClick={() => load()}>{uiText("Try again")}</button>}
      {state === "ready" && (
        <section className="org-workspace">
          {visibleCount === 0 ? (
            <div className="org-empty">
              <Search />
              <h2>No matching units</h2>
              <p>Try another name or code, or clear the current search.</p>
              <button onClick={() => setQuery("")}>{uiText("Clear search")}</button>
            </div>
          ) : view === "tree" ? (
            <div
              className="org-canvas"
              ref={canvasRef}
              onPointerDown={startPan}
              onPointerMove={pan}
              onPointerUp={stopPan}
              onPointerCancel={stopPan}
              aria-label="Organization chart canvas. Drag the background to pan."
            >
              <p className="org-canvas-help">
                Drag the background to pan
                {canManage
                  ? "; drag a unit onto its new parent to move it"
                  : ""}
                .
              </p>
              <ul className="org-tree" style={{ transform: `scale(${zoom})` }}>
                {tree.map((n) => (
                  <Node
                    key={n.id}
                    {...{
                      node: n,
                      selected,
                      setSelected,
                      collapsed,
                      setCollapsed,
                      query,
                      canManage,
                      draggedUnit,
                      dropTarget,
                      setDraggedUnit,
                      setDropTarget,
                      onDropUnit: moveByDrop,
                    }}
                  />
                ))}
              </ul>
            </div>
          ) : (
            <div className="org-table">
              <table>
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th>{uiText("Type")}</th>
                    <th>Parent</th>
                    <th>Classification</th>
                    <th>Head</th>
                    <th>{uiText("Status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {flat
                    .filter((x) => subtreeMatches(x, query))
                    .map((x) => (
                      <tr
                        onClick={() => setSelected(x)}
                        key={x.id}
                        className={!x.is_active ? "archived" : ""}
                      >
                        <td>{x.name}</td>
                        <td>{x.unit_type}</td>
                        <td>{x.parentName || "—"}</td>
                        <td>{x.classification || "—"}</td>
                        <td>
                          {x.unitHead?.status === "RESOLVED"
                            ? x.unitHead.userName
                            : x.unitHead?.status === "AMBIGUOUS"
                              ? "Ambiguous"
                              : uiText("Unassigned")}
                        </td>
                        <td>{x.is_active ? "ACTIVE" : "ARCHIVED"}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
          {selected && (
            <aside>
              <button
                aria-label="Close details"
                className="close"
                onClick={() => setSelected(null)}
              >
                ×
              </button>
              <h2>{selected.name}</h2>
              {selected.authorityError && (
                <p className="form-error" role="alert">
                  Configuration error: {selected.authorityError}
                </p>
              )}
              <dl>
                <dt>{uiText("Type")}</dt>
                <dd>{selected.unit_type}</dd>
                <dt>Classification</dt>
                <dd>{selected.classification || "Not set"}</dd>
                <dt>Reports To</dt>
                <dd>{selected.parent_name || selected.parentName || "Root"}</dd>
                <dt>Full path</dt>
                <dd>
                  {selected.path?.join(" › ") ||
                    selected.displayPath?.join(" › ") ||
                    selected.name}
                </dd>
                <dt>Linked identity</dt>
                <dd>
                  {selected.department_name || selected.section_name || "None"}
                </dd>
                <dt>Unit Head</dt>
                <dd>
                  {selected.unitHead?.status === "RESOLVED"
                    ? selected.unitHead.userName
                    : selected.unitHead?.status === "AMBIGUOUS"
                      ? "Ambiguous"
                      : uiText("Unassigned")}
                </dd>
                <dt>Executive Owner</dt>
                <dd>
                  {selected.executiveOwner?.status === "RESOLVED"
                    ? selected.executiveOwner.userName
                    : selected.executiveOwner?.status === "AMBIGUOUS"
                      ? "Ambiguous"
                      : uiText("Unassigned")}
                </dd>
                <dt>{uiText("Status")}</dt>
                <dd>{selected.is_active ? "ACTIVE" : "ARCHIVED"}</dd>
                {selected.unit_type === "DEPARTMENT" && (
                  <>
                    <dt>Approval Routing Readiness</dt>
                    <dd>
                      <span
                        className={`status ${selected.approvalRoutingReadiness?.status === "READY" ? "active" : "vacant"}`}
                      >
                        {selected.approvalRoutingReadiness?.status ||
                          "INCOMPLETE"}
                      </span>
                      {selected.approvalRoutingReadiness?.reasons?.length >
                        0 && (
                        <small>
                          {selected.approvalRoutingReadiness.reasons.join(", ")}
                        </small>
                      )}
                    </dd>
                  </>
                )}
              </dl>
              <h3>Current positions</h3>
              {(selected.positions || [])
                .filter((p) => positionStatus(p) === "ACTIVE")
                .map((p) => (
                  <p key={p.id}>
                    {p.position_name}: {p.user_name || uiText("Unassigned")}
                  </p>
                ))}
              {canManage && (
                <div className="aside-actions">
                  <button onClick={() => setDialog("edit")}>Edit Unit</button>
                  <button onClick={() => setDialog("move")}>Move Unit</button>
                  <button onClick={() => setDialog("head")}>
                    Assign / Change Head
                  </button>
                  <button onClick={() => setDialog("positions")}>
                    Manage Positions
                  </button>
                  {selected.is_active && (
                    <button className="danger" onClick={archive}>
                      Archive
                    </button>
                  )}
                </div>
              )}
            </aside>
          )}
        </section>
      )}
      {canManage && dialog === "add" && (
        <UnitForm
          mode="add"
          units={parentUnits}
          options={options}
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}{" "}
      {canManage && dialog === "edit" && (
        <UnitForm
          mode="edit"
          unit={selected}
          units={parentUnits}
          options={options}
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}{" "}
      {canManage && dialog === "move" && (
        <MoveDialog
          unit={selected}
          units={parentUnits}
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}{" "}
      {canManage && dialog === "head" && (
        <HeadDialog
          unit={selected}
          options={options}
          onClose={() => setDialog(null)}
          onSaved={saved}
        />
      )}{" "}
      {canManage && dialog === "reconciliation" && (
        <ReconciliationDialog
          onClose={() => setDialog(null)}
          onChanged={() => load(selected?.id)}
        />
      )}{" "}
      {canManage && dialog === "positions" && (
        <PositionsDialog
          unit={selected}
          options={options}
          onClose={() => setDialog(null)}
          onChanged={() =>
            org.getOrganizationUnit(selected.id).then(setSelected)
          }
        />
      )}
    </main>
  );
}
